import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PromiseStatuses } from '../types';
import { TargetSection } from '../utils';
import { ContactType, FindAllContactsFilters, FindContactsTargetResponse, SectionTargetContacts, TargetResponse } from './dto';
import { NewContactsListService } from './service';
import axios from 'axios';

const targetControllers: Record<string, AbortController> = {};

const emptyResponsePerson: TargetResponse<ContactType.PERSON> = {
    response: {
        data: [],
        total: 0,
        page: 0,
        perPage: 25,
        prev: false,
        next: false,
        totalPage: 0
    },
    status: 'idle'
}

const emptyResponseCompany: TargetResponse<ContactType.COMPANY> = {
    response: {
        data: [],
        total: 0,
        page: 0,
        perPage: 25,
        prev: false,
        next: false,
        totalPage: 0
    },
    status: 'idle'
}

const initialState: SectionTargetContacts = {
    target: {
        PERSON: emptyResponsePerson,
        COMPANY: emptyResponseCompany
    },
    "add-target-tl": {
        PERSON: emptyResponsePerson,
        COMPANY: emptyResponseCompany
    },
    "target-bin": {
        PERSON: emptyResponsePerson,
        COMPANY: emptyResponseCompany
    },
};

export const findContactsTarget = createAsyncThunk<
    { response: FindContactsTargetResponse<ContactType>; section: TargetSection; contactType: ContactType },
    { filters: FindAllContactsFilters; section: TargetSection; contactType: ContactType },
    { rejectValue: "aborted" }
>(
    "contacts/findContacts",
    async (request, thunkApi) => {
        const key = `${request.section}-${request.contactType}`;

        if (targetControllers[key]) {
            targetControllers[key].abort();
        }
        const controller = new AbortController();
        targetControllers[key] = controller;

        const ContactsService = NewContactsListService();

        try {
            const response = await ContactsService.findContactsTarget(
                request.filters,
                request.contactType,
                controller.signal
            );

            return {
                response,
                section: request.section,
                contactType: request.contactType,
            };
        } catch (err: any) {
            if (
                axios.isCancel?.(err) ||
                err.name === "CanceledError" ||
                err.name === "AbortError"
            ) {
                return thunkApi.rejectWithValue("aborted");
            }
            throw err;
        }
    }
);

const contactsSlice = createSlice({
    name: 'targetContacts',
    initialState,
    reducers: {
        setFindContactsStatus: (
            state,
            action: PayloadAction<{ section: TargetSection; status: PromiseStatuses, contactType: ContactType }>
        ) => {
            const { section, status, contactType } = action.payload;

            state[section] = {
                ...state[section],
                [contactType]: {
                    response: state[section][contactType].response,
                    status,
                }
            };
        },
    },
    extraReducers(builder) {
        builder.addCase(findContactsTarget.fulfilled, (state, action) => {
            const { section, response, contactType } = action.payload;

            state[section] = {
                ...state[section],
                [contactType]: {
                    response,
                    status: 'successfully',
                }
            };
        });

        builder.addCase(findContactsTarget.pending, (state, action) => {
            const { section, contactType } = action.meta.arg;

            state[section] = {
                ...state[section],
                [contactType]: {
                    response: {
                        data: [],
                        total: 0,
                        page: 0,
                        perPage: 25,
                        prev: false,
                        next: false,
                        totalPage: 0
                    },
                    status: 'loading',
                }
            };
        });

        builder.addCase(findContactsTarget.rejected, (state, action) => {
            if (action.payload === "aborted") {
                return;
            }

            const { section, contactType } = action.meta.arg;

            state[section] = {
                ...state[section],
                [contactType]: {
                    response: {
                        data: [],
                        total: 0,
                        page: 0,
                        perPage: 25,
                        prev: false,
                        next: false,
                        totalPage: 0
                    },
                    status: "failed",
                }
            };
        });
    },
});

export const { setFindContactsStatus } = contactsSlice.actions;
export default contactsSlice.reducer;
